/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lng1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lng2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  if (!lat1 || !lng1 || !lat2 || !lng2) {
    return Infinity; // Return large distance if coordinates are missing
  }

  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * Convert degrees to radians
 */
function toRad(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Check if two locations are within a certain radius
 * @param {number} lat1 - Latitude of first point
 * @param {number} lng1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lng2 - Longitude of second point
 * @param {number} radiusKm - Radius in kilometers (default: 5)
 * @returns {boolean} True if within radius
 */
export function isWithinRadius(lat1, lng1, lat2, lng2, radiusKm = 5) {
  const distance = calculateDistance(lat1, lng1, lat2, lng2);
  return distance <= radiusKm;
}

/**
 * Find the closest location from a list of locations
 * @param {number} targetLat - Target latitude
 * @param {number} targetLng - Target longitude
 * @param {Array} locations - Array of location objects with lat/lng
 * @returns {Object|null} Closest location with distance, or null if no valid locations
 */
export function findClosestLocation(targetLat, targetLng, locations) {
  if (!targetLat || !targetLng || !locations || locations.length === 0) {
    return null;
  }

  let closest = null;
  let minDistance = Infinity;

  locations.forEach((location) => {
    const lat = location.lat || location.pickup_lat || location.location_lat;
    const lng = location.lng || location.pickup_lng || location.location_lng;
    
    if (lat && lng) {
      const distance = calculateDistance(targetLat, targetLng, lat, lng);
      if (distance < minDistance) {
        minDistance = distance;
        closest = { ...location, distance };
      }
    }
  });

  return closest;
}
